/**
 * useProgramLabels — LIVE program name/icon resolver for a page's program ids.
 *
 * WHY: the static `programRegistry` (getProgramMeta) only knows ~26 well-known
 * native/SPL/Metaplex/XDEX programs. The BUSY long tail of X1-native programs
 * renders as a generic gray `Cpu` gear with a truncated id. Meanwhile the
 * backend keeps a LIVE, self-updating catalog (`program_labels_latest`, grown
 * automatically by the on-chain security.txt backfill) with real names +
 * categories for many more programs. This hook batch-reads that catalog for the
 * ids visible on a page and merges it with the static registry.
 *
 * RESOLUTION PRECEDENCE (getProgramDisplay):
 *   1. static `getProgramMeta(id)` for ids the curated registry KNOWS — these
 *      win so System / SPL Token / ATA / XDEX keep their guaranteed name +
 *      specific lucide icon + color. (verified flag still reflects the live
 *      catalog when present, so well-known programs can show a check too.)
 *   2. else the LIVE catalog label: `name` from the catalog + an Icon/color
 *      DERIVED FROM `category` (see categoryDisplay). New programs the backfill
 *      names later then appear automatically with NO frontend change.
 *   3. else fallback: truncated id + generic `Cpu` (current behavior) — also the
 *      graceful state WHILE the query is loading, so a row is never blank.
 *
 * The hook is sync-friendly for render: it returns a stable `getProgramDisplay`
 * lookup that reads from the resolved labels map; labels upgrade in-place when
 * the react-query fetch resolves. No new deps (react-query already present).
 */
import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Repeat2,
  Server,
  Database,
  Landmark,
  Coins,
  Image as ImageIcon,
  Lock,
  Settings,
  Cpu,
  type LucideIcon,
} from 'lucide-react'
import { getProgramMeta, PROGRAM_REGISTRY } from '@/utils/programRegistry'

/** One catalog row as returned by GET /api/programs/labels. */
export interface LiveProgramLabel {
  name: string
  category: string
  /** Explorer-served logo path (e.g. /brand/x1-market/icon.svg) when the program
   *  ships curated/on-chain brand art. Empty for the long tail. */
  icon_url?: string
  verified: boolean
}

/** Merged display metadata for a program id (render-ready). */
export interface ProgramDisplay {
  name: string
  Icon: LucideIcon
  color: string
  /** When set, render this image instead of the lucide `Icon` — the program's
   *  real logo (curated registry icon_uri or an on-chain logo). */
  iconUrl?: string
  verified: boolean
}

/**
 * Category → { Icon, color } derivation for LIVE catalog labels. Colors are the
 * exact Tailwind text-color classes from the programRegistry palette (copied,
 * theme-aware light/dark). Matching is case-insensitive + substring-based so the
 * catalog can grow new category spellings (DEX / AMM / Swap all → DeFi) without
 * a code change — the sustainability contract.
 */
const CAT_COLOR = {
  defi: 'text-indigo-700 dark:text-indigo-400',
  infra: 'text-violet-700 dark:text-violet-400',
  oracle: 'text-cyan-700 dark:text-cyan-400',
  governance: 'text-teal-700 dark:text-teal-400',
  token: 'text-orange-700 dark:text-orange-400',
  nft: 'text-pink-700 dark:text-pink-400',
  stake: 'text-green-700 dark:text-green-400',
  system: 'text-blue-700 dark:text-blue-400',
  program: 'text-violet-700 dark:text-violet-400',
} as const

/** Fallback for a labelled program whose category we don't specifically map. */
const CATEGORY_FALLBACK: { Icon: LucideIcon; color: string } = {
  Icon: Cpu,
  color: CAT_COLOR.program,
}

/**
 * Derive an icon + color from a catalog `category` string. Substring,
 * case-insensitive, ordered most-specific-first.
 */
export function categoryDisplay(category: string | undefined | null): {
  Icon: LucideIcon
  color: string
} {
  const c = (category || '').toLowerCase()
  if (!c) return CATEGORY_FALLBACK
  if (/(defi|dex|amm|swap|liquid|market)/.test(c)) return { Icon: Repeat2, color: CAT_COLOR.defi }
  if (/(oracle|price|feed)/.test(c)) return { Icon: Database, color: CAT_COLOR.oracle }
  if (/(govern|dao|multisig|timelock|vote)/.test(c)) return { Icon: Landmark, color: CAT_COLOR.governance }
  if (/(nft|metadata|metaplex|collectible)/.test(c)) return { Icon: ImageIcon, color: CAT_COLOR.nft }
  if (/(stak|vault|vesting|escrow)/.test(c)) return { Icon: Lock, color: CAT_COLOR.stake }
  if (/(token|mint|spl)/.test(c)) return { Icon: Coins, color: CAT_COLOR.token }
  if (/(infra|network|node|rpc|bridge|data)/.test(c)) return { Icon: Server, color: CAT_COLOR.infra }
  if (/(system|native|config)/.test(c)) return { Icon: Settings, color: CAT_COLOR.system }
  return CATEGORY_FALLBACK
}

/** Ids per bulk request — mirrors the backend PROGRAM_LABELS_BULK_MAX cap. */
const BATCH_SIZE = 100
/** Catalog changes slowly (security.txt backfill) — cache generously. */
const STALE_TIME_MS = 5 * 60 * 1000

async function fetchProgramLabels(ids: string[]): Promise<Record<string, LiveProgramLabel>> {
  if (ids.length === 0) return {}
  // Chunk into <=BATCH_SIZE groups so a page with a huge unique-id set still
  // resolves fully (the endpoint caps each request).
  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += BATCH_SIZE) chunks.push(ids.slice(i, i + BATCH_SIZE))

  const merged: Record<string, LiveProgramLabel> = {}
  await Promise.all(
    chunks.map(async (chunk) => {
      try {
        const res = await fetch(`/api/programs/labels?ids=${encodeURIComponent(chunk.join(','))}`)
        if (!res.ok) return
        const obj = (await res.json()) as Record<string, LiveProgramLabel>
        if (obj && typeof obj === 'object') Object.assign(merged, obj)
      } catch {
        // Best-effort: a failed chunk simply leaves those ids on the static/Cpu
        // fallback. Never throw — the UI must not break on a label miss.
      }
    }),
  )
  return merged
}

export interface UseProgramLabelsResult {
  /** Resolved live catalog labels keyed by program id (only non-empty names). */
  labels: Record<string, LiveProgramLabel>
  /** True while the catalog fetch is in flight. */
  isLoading: boolean
  /** Render-ready resolver applying the static → live → fallback precedence. */
  getProgramDisplay: (programId: string) => ProgramDisplay
}

/**
 * Batch-resolve LIVE program labels for a set of ids, exposing a
 * `getProgramDisplay` merger. Dedupes + sorts ids for a stable react-query key
 * (so the same id-set shares one cache entry). Empty/short id-sets skip the
 * fetch entirely.
 */
export function useProgramLabels(programIds: string[]): UseProgramLabelsResult {
  const ids = useMemo(
    () => [...new Set((programIds || []).filter((id) => typeof id === 'string' && id.length > 0))].sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(programIds || []).join(',')],
  )

  const { data, isLoading } = useQuery({
    queryKey: ['program-labels', ids],
    queryFn: () => fetchProgramLabels(ids),
    enabled: ids.length > 0,
    staleTime: STALE_TIME_MS,
    gcTime: STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  const labels = data ?? {}

  const getProgramDisplay = useCallback(
    (programId: string): ProgramDisplay => {
      const id = programId ?? ''
      const live = id ? labels[id] : undefined

      // 1. Curated static registry WINS for name + specific icon + color. A LIVE
      //    catalog icon_url (curated/on-chain logo) still upgrades the glyph to
      //    real brand art when present.
      const staticMeta = id ? PROGRAM_REGISTRY[id] : undefined
      if (staticMeta) {
        return {
          name: staticMeta.name,
          Icon: staticMeta.Icon,
          color: staticMeta.color,
          iconUrl: live?.icon_url || staticMeta.logoUrl || undefined,
          verified: live?.verified ?? false,
        }
      }

      // 2. LIVE catalog label → name + real logo (icon_url) or category-derived
      //    icon/color. New programs the backend names/brands later then appear
      //    automatically with NO frontend change.
      if (live && live.name) {
        const { Icon, color } = categoryDisplay(live.category)
        return { name: live.name, Icon, color, iconUrl: live.icon_url || undefined, verified: !!live.verified }
      }

      // 3. Fallback: truncated id + generic Cpu (also the loading state).
      const meta = getProgramMeta(id)
      return { name: meta.name, Icon: meta.Icon, color: meta.color, verified: false }
    },
    // labels identity changes when the query resolves → resolver upgrades in-place.
    [labels],
  )

  return { labels, isLoading, getProgramDisplay }
}

export default useProgramLabels
